import React from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';

import {ModalFirsError} from 'components/common/ModalFirsError';
import {Page} from 'components/common/Page';
import {PermissionAccess} from 'components/common/PermissionAccess';
import {DateRange} from 'components/ui-kit/DateRange';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {Select} from 'components/ui-kit/Select';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {isAdmin$, isCustomsRole$} from 'models/auth';
import {
    DocumentsGridRawRow,
    GridFieldConfig,
    MovingAccessors,
    MovingGridFormattedRow,
    MovingGridRawRow,
    UserPermission,
} from 'types';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {mapArrayToI18nOptions} from 'utils/select';

import {Controls} from '../DocumentsGrid/Controls';
import {GridsNavigation} from '../GridsNavigation';

import {ControlsWrapper, HeaderControls, NavigationWrapper} from './styled';
import {MovingGridTable} from './Table';
import {TableSearch} from './Table/search';

const getConfig = (t: TFunction, isAdmin: boolean): GridFieldConfig[] => {
    const id = {
        id: MovingAccessors.id,
        header: t('pages.documents.documents.id'),
        isSortable: false,
        width: 300,
    };

    const createdDate = {
        id: MovingAccessors.createdDate,
        header: t('pages.documents.documents.createdDate'),
        filter: <DateRange />,
        isSortable: true,
    };

    const movingFrom = {
        id: MovingAccessors.storageLocationFrom,
        header: t('pages.documents.documents.movingFrom'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
    };

    const movingTo = {
        id: MovingAccessors.storageLocationTo,
        header: t('pages.documents.documents.movingTo'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: true,
    };

    const employeeFullName = {
        id: MovingAccessors.employeeFullName,
        header: t('pages.documents.documents.employeeFullName'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: false,
    };

    const tin = {
        id: MovingAccessors.tin,
        header: t('pages.documents.documents.tin'),
        filter: <Input placeholder={t('Enter value')} />,
        isSortable: false,
    };

    const status = {
        id: MovingAccessors.status,
        header: t('pages.documents.documents.status'),
        filter: (
            <Select
                options={mapArrayToI18nOptions(
                    Object.keys(DocumentStatus)
                        .filter((s) => ['DRAFT', 'PROCESSED', 'REJECTED'].includes(s))
                        .map((s) => ({value: s, key: s})),
                    'dictionaries.documents.status',
                )}
                isClearable
            />
        ),
        isSortable: false,
    };

    const fields = [id, createdDate, movingFrom, movingTo, employeeFullName, status];
    if (isAdmin) fields.push(tin);

    return fields;
};

export function convertData(
    data: MovingGridRawRow[] | undefined,
    t: Function,
    lastCreatedDocumentId?: string | null,
    updOnly?: boolean,
): MovingGridFormattedRow[] {
    if (!data) return [];

    const getColors = (status: DocumentStatus) => ({
        color: DOCUMENT_STATUS_COLORS[status ?? 'DEFAULT'],
    });

    return data.map((elem) => ({
        [MovingAccessors.id]: handleNil(elem, (value: DocumentsGridRawRow) => (
            <PermissionAccess
                permissions={[UserPermission.DOCUMENT_DETAIL_VIEW]}
                placeholder={value.reportId}
            >
                <Link
                    to={{
                        pathname: updOnly
                            ? generatePath(ROUTE.INVOICES_DETAIL, {id: value.reportId})
                            : generatePath(ROUTE.DOCUMENTS_DETAIL, {id: value.reportId}),
                        state: {from: location.pathname},
                    }}
                >
                    <span
                        style={{
                            fontWeight: value.reportId === lastCreatedDocumentId ? 'bold' : 'unset',
                        }}
                    >
                        {value.reportId}
                    </span>
                </Link>
            </PermissionAccess>
        )),
        [MovingAccessors.createdDate]: handleNil(elem.createdAt, (value: string) =>
            formatDate(new Date(value), 'dd.MM.yyyy HH:mm'),
        ),
        [MovingAccessors.storageLocationFrom]: handleNil(elem.storageLocationFrom),
        [MovingAccessors.storageLocationTo]: handleNil(elem.storageLocationTo),
        [MovingAccessors.employeeFullName]: handleNil(elem.employeeFullName),
        [MovingAccessors.tin]: handleNil(elem.tin),
        [MovingAccessors.status]: handleNil(elem.status, (value: DocumentStatus) => (
            <StatusWrapper {...getColors(value)}>
                {t('dictionaries.documents.status.' + value)}
            </StatusWrapper>
        )),
    }));
}

export const MovingGridPage = () => {
    const {t} = useTranslation();
    const isAdmin = useStore(isAdmin$);
    const isCustomsRole = useStore(isCustomsRole$);

    const headerControls = (
        <HeaderControls>
            <ControlsWrapper>
                <TableSearch />
                {!isCustomsRole && <Controls />}
            </ControlsWrapper>
            <NavigationWrapper needMargin>
                {!isCustomsRole && <GridsNavigation />}
            </NavigationWrapper>
        </HeaderControls>
    );

    return (
        <Page title={t('menu.Documents')}>
            <ModalFirsError />
            <MovingGridTable
                configFunc={(translate) => getConfig(translate, !!isAdmin)}
                dataMapper={convertData}
                headerControls={headerControls}
                headerControlsAlignItems="flex-end"
            />
        </Page>
    );
};
